import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { CheckRounded, CloseRounded, EditOutlined } from "@mui/icons-material";
import { useDashboardColors } from "../../HomePage/dashboardColors";

/** Same limit as the working title of the create form. */
export const TITLE_MAX = 200;

interface Props {
  title: string;
  canEdit: boolean;
  pending: boolean;
  /** Resolves when saved; the editor stays open on errors. */
  onSave: (title: string) => Promise<unknown>;
}

/** The document title; editors change it in place (no new revision). */
const EditableTitle: React.FC<Props> = ({ title, canEdit, pending, onSave }) => {
  const { t } = useTranslation("documentCrafting");
  const { primary } = useDashboardColors();
  const [draft, setDraft] = useState<string | null>(null);

  const value = draft ?? title;
  const trimmed = value.trim();
  const invalid = !trimmed || trimmed.length > TITLE_MAX;

  const submit = () => {
    if (invalid || pending) return;
    if (trimmed === title) {
      setDraft(null);
      return;
    }
    onSave(trimmed)
      .then(() => setDraft(null))
      .catch(() => undefined);
  };

  if (draft === null) {
    return (
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            fontSize: 26,
            fontWeight: 800,
            lineHeight: "34px",
            color: primary,
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Typography>
        {canEdit && (
          <Tooltip title={t("detail.header.editTitle")} arrow>
            <IconButton
              size="small"
              onClick={() => setDraft(title)}
              aria-label={t("detail.header.editTitle")}
              sx={{ mt: "3px" }}
            >
              <EditOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={(event: React.FormEvent) => {
        event.preventDefault();
        submit();
      }}
      sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, maxWidth: 720 }}
    >
      <TextField
        autoFocus
        fullWidth
        size="small"
        label={t("detail.header.titleLabel")}
        value={value}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setDraft(null);
        }}
        error={invalid}
        helperText={
          !trimmed
            ? t("detail.header.titleRequired")
            : trimmed.length > TITLE_MAX
              ? t("detail.header.titleTooLong", { max: TITLE_MAX })
              : undefined
        }
        disabled={pending}
        inputProps={{ maxLength: TITLE_MAX + 20 }}
      />
      <IconButton
        type="submit"
        disabled={invalid || pending}
        aria-label={t("common.save")}
      >
        <CheckRounded />
      </IconButton>
      <IconButton
        onClick={() => setDraft(null)}
        disabled={pending}
        aria-label={t("common.cancel")}
      >
        <CloseRounded />
      </IconButton>
    </Box>
  );
};

export default EditableTitle;
